import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  useLazyGetResultOfApplyUpsellQuery,
  usePatchSignatureUpsellMutation,
  usePatchUpsellMutation,
} from '@greenkub-fronts/data-access';
import {
  paginateItems,
  useCustomDebounce,
} from '@greenkub-fronts/shared/utils';
import {
  addErrorMessage,
  addPositiveMessage,
} from '@greenkub-fronts/domain/snackbar';
import {
  AppDispatch,
  FixMe,
  RootState,
  Upsell,
} from '@greenkub-fronts/shared/interface';
import {
  getCurrentProject,
  openModalSignature,
} from '@greenkub-fronts/domain/signature';
import { LoaderCube } from '@greenkub-fronts/shared/ui';

import Amendment from '../../../ui/web/template/amendment/amendment';

export interface AmendmentFeatureProps {
  sign1Ref: FixMe;
  sign2Ref: FixMe;
}

const AmendmentFeature = (props: AmendmentFeatureProps) => {
  const { sign1Ref, sign2Ref } = props;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { upsell, isSignature, signature, signatures } = useSelector(
    (state: RootState) => state.signature
  );

  const project = useSelector((state: RootState) => getCurrentProject(state));

  /**
   * STATE
   */
  const [getResultOfApplyUpsell] = useLazyGetResultOfApplyUpsellQuery();
  const [patchUpsell] = usePatchUpsellMutation();
  const [patchSignature, patchSignatureQuery] =
    usePatchSignatureUpsellMutation();

  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalVat, setTotalVat] = useState<number>(0);
  const [totalPriceWithVat, setTotalPriceWithVat] = useState<number>(0);
  const [totalVatAdjusted, setTotalVatAdjusted] = useState<number>(0);
  const [totalPriceWithVatAdjusted, setTotalPriceWithVatAdjusted] =
    useState<number>(0);
  const [adjustment, setAdjustment] = useState<number>();
  const [comment, setComment] = useState<string>();
  const [bdcAfterAvenant, setBdcAfterAvenant] = useState([]);
  const [paginatedBDCAfterAvenant, setPaginatedBDCAfterAvenant] = useState([]);
  const [projectAdjustment, setProjectAdjustment] = useState<number>(0);
  const [totalProjectVatAdjusted, setTotalProjectVatAdjusted] = useState(0);
  const [
    totalPriceProjectWithVatAdjusted,
    setTotalPriceProjectWithVatAdjusted,
  ] = useState(0);
  const [items, setItems] = useState([]);
  const [itemPages, setItemPages] = useState([[]]);

  /**
   * INIT
   */
  useEffect(() => {
    if (!upsell) return;
    if (!comment) {
      setComment(upsell?.notes);
    }
    if (!adjustment) {
      setAdjustment(upsell?.adjustement);
    }

    setTotalPrice(upsell.totalPrice);
    setTotalVat(upsell.totalVat);
    setTotalPriceWithVat(upsell.totalPriceWithVat);

    setTotalVatAdjusted(upsell.totalVat - upsell.adjustment / 5);
    setTotalPriceWithVatAdjusted(
      upsell.totalPriceWithVat - (upsell.adjustment / 5) * 6
    );

    setItems(upsell.itemsLines);
    setItemPages(paginateItems(upsell.itemsLines));
  }, [upsell]);

  useEffect(() => {
    if (upsell) getConfigWithUpsellApplied(upsell);
  }, [upsell]);

  const getConfigWithUpsellApplied = (upsell: Upsell) => {
    getResultOfApplyUpsell({ upsellId: upsell.id }).then((resp) => {
      if (resp.error) {
        dispatch(addErrorMessage(resp.error));
      } else {
        setBdcAfterAvenant(resp.data);
        setPaginatedBDCAfterAvenant(
          paginateItems(resp.data.itemsLines) as FixMe
        );

        setProjectAdjustment(project?.adjustment ? project.adjustment : 0);
        setTotalProjectVatAdjusted(
          project?.adjustment
            ? resp.data.totalVat - project.adjustment / 5
            : resp.data.totalVat
        );
        setTotalPriceProjectWithVatAdjusted(
          project?.adjustment
            ? resp.data.totalPriceWithVat - (project.adjustment / 5) * 6
            : resp.data.totalPriceWithVat
        );
      }
    });
  };

  /**
   * EDITIONS
   */
  const updateData = () => {
    if (isSignature) return;
    patchUpsell({
      upsellId: upsell?.id,
      upsell: {
        adjustment,
        notes: comment,
      },
    }).then((resp) => {
      if (resp.error) {
        dispatch(addErrorMessage(resp.error));
      } else {
        dispatch(addPositiveMessage(resp.data));
      }
    });
  };

  const updateAdjustment = (newAdjustment: number) => {
    setTotalVatAdjusted(totalVat - newAdjustment / 5);
    setTotalPriceWithVatAdjusted(totalPriceWithVat - (newAdjustment / 5) * 6);
    setAdjustment(newAdjustment);
  };

  useCustomDebounce(
    async () => {
      if (isSignature) return;
      updateData();
    },
    2000,
    [comment, adjustment]
  );

  /**
   * COMPUTE DATA
   */
  const isKit = useMemo(() => {
    return (
      project?.configurations[0].chosenStudioList?.studio?.model?.name.includes(
        'KIT'
      ) || false
    );
  }, []);

  const triggerUpsells = () => {
    if (patchSignatureQuery.isLoading) return;
    const result = window.confirm(
      "En employant cette option, vous allez appliquer les actions de l'upsell sans passer par la signature, ni génération de document. Cette option ne devrait être utilisé que pour corriger une incohérence entre les données du projet et les documents BDC et Avenant"
    );
    if (!result) return;
    patchSignature({
      upsellId: upsell?.id,
      data: { justForUpdate: true },
    }).then((resp: FixMe) => {
      if (resp.error) {
        dispatch(addErrorMessage(resp.error));
      } else {
        dispatch(addPositiveMessage('Upsell mis à jour'));
        navigate(`/config/${project?.id}`);
      }
    });
  };

  if (project === undefined || patchSignatureQuery.isLoading)
    return <LoaderCube isLoading={true} />;

  return (
    <Amendment
      isKit={isKit}
      project={project}
      isSignature={isSignature}
      signature={signature}
      signatures={signatures}
      sign1Ref={sign1Ref}
      sign2Ref={sign2Ref}
      handleOpenModalSignHandler={(index: number) =>
        dispatch(openModalSignature({ currentIndexSignature: index }))
      }
      address={project?.address}
      triggerUpsells={triggerUpsells}
      itemPages={itemPages as FixMe}
      totalPrice={totalPrice}
      adjustment={adjustment as FixMe}
      updateAdjustment={updateAdjustment}
      totalVatAdjusted={totalVatAdjusted}
      totalPriceWithVatAdjusted={totalPriceWithVatAdjusted}
      comment={comment || ''}
      setComment={setComment}
      paginatedBDCAfterAvenant={paginatedBDCAfterAvenant}
      bdcAfterAvenant={bdcAfterAvenant}
      projectAdjustment={projectAdjustment}
      totalProjectVatAdjusted={totalProjectVatAdjusted}
      totalPriceProjectWithVatAdjusted={totalPriceProjectWithVatAdjusted}
    />
  );
};

export default AmendmentFeature;
